import { Controller } from "@hotwired/stimulus"
import { t } from "../lib/i18n"

export default class extends Controller {
  static values = {
    inviteUuid: String,
    bookId: Number,
  }

  declare inviteUuidValue: string
  declare bookIdValue: number

  private selectedChoices: Map<number, string> = new Map()
  private isSaving: boolean = false
  private saveQueued: boolean = false
  private queuedSilent: boolean = true

  connect() {
    this.loadExistingResponses()
    document.addEventListener(
      "participant:saveSelectionsBeforeSubmit",
      this.saveBeforeSubmitHandler,
    )
  }

  disconnect() {
    document.removeEventListener(
      "participant:saveSelectionsBeforeSubmit",
      this.saveBeforeSubmitHandler,
    )
  }

  private saveBeforeSubmitHandler = (event: Event) => {
    const customEvent = event as CustomEvent<{ silent?: boolean }>
    this.saveAllChoices(customEvent.detail?.silent ?? false)
  }

  selectChoice(event: Event) {
    const target = event.currentTarget as HTMLElement
    const choiceId = parseInt(target.dataset.choiceId || "0", 10)
    const option = target.dataset.option || ""

    if (!choiceId || !option) {
      console.error("Invalid choice data", { choiceId, option })
      return
    }

    this.selectedChoices.set(choiceId, option)

    this.updateChoiceButtons(choiceId, option)
    this.saveAllChoices(true)
  }

  confirmSelection() {
    this.saveAllChoices(false)
  }

  private loadExistingResponses() {
    const choiceRows = this.element.querySelectorAll("[data-choice-id]")
    const selectedColorRgb = "rgb(237, 244, 242)"

    choiceRows.forEach((row) => {
      const choiceId = parseInt((row as HTMLElement).dataset.choiceId || "0", 10)
      if (choiceId) {
        const optionAButton = row.querySelector('[data-option="A"]') as HTMLElement
        const optionBButton = row.querySelector('[data-option="B"]') as HTMLElement

        const isSelected = (element: HTMLElement): boolean => {
          const bgColor = window.getComputedStyle(element).backgroundColor
          return (
            bgColor === selectedColorRgb ||
            (bgColor.toLowerCase().includes("237") &&
              bgColor.includes("244") &&
              bgColor.includes("242"))
          )
        }

        if (optionAButton && isSelected(optionAButton)) {
          this.selectedChoices.set(choiceId, "A")
        } else if (optionBButton && isSelected(optionBButton)) {
          this.selectedChoices.set(choiceId, "B")
        }
      }
    })
  }

  private updateChoiceButtons(choiceId: number, selectedOption: string) {
    const row = this.element.querySelector(`[data-choice-id="${choiceId}"]`) as HTMLElement
    if (!row) return

    const optionAButton = row.querySelector('[data-option="A"]') as HTMLElement
    const optionBButton = row.querySelector('[data-option="B"]') as HTMLElement

    if (optionAButton && optionBButton) {
      const selectedColor = "#EDF4F2"
      const unselectedColor = "#EEECF6"
      const borderRadius = "0.5rem"

      optionAButton.style.backgroundColor = unselectedColor
      optionAButton.style.borderRadius = borderRadius
      optionBButton.style.backgroundColor = unselectedColor
      optionBButton.style.borderRadius = borderRadius

      if (selectedOption === "A") {
        optionAButton.style.backgroundColor = selectedColor
      } else if (selectedOption === "B") {
        optionBButton.style.backgroundColor = selectedColor
      }
    }
  }

  private getAllChoiceIds(): number[] {
    const choiceRows = this.element.querySelectorAll("[data-choice-id]")
    return Array.from(choiceRows)
      .map((row) => {
        return parseInt((row as HTMLElement).dataset.choiceId || "0", 10)
      })
      .filter((id) => id > 0)
  }

  private async saveChoice(choiceId: number, option: string) {
    if (this.isSaving) {
      return
    }

    this.isSaving = true

    try {
      const formData = new FormData()
      formData.append("two_sided_voting_choice_id", choiceId.toString())
      formData.append("choice", option)
      formData.append("invite_uuid", this.inviteUuidValue)
      formData.append("book_id", this.bookIdValue.toString())
      formData.append("authenticity_token", this.getAuthenticityToken())

      const response = await fetch("/participant/save_voting_response", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getAuthenticityToken(),
          Accept: "application/json",
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to save choice: ${response.statusText}`)
      }

      const result = await response.json()
      if (result.status !== "ok") {
        console.error("Error saving choice:", result.error)
      }
    } catch (error) {
      console.error("Error saving choice:", error)
    } finally {
      this.isSaving = false
    }
  }

  private async saveAllChoices(silent: boolean = false) {
    if (this.isSaving) {
      this.saveQueued = true
      this.queuedSilent = this.queuedSilent && silent
      return
    }

    this.isSaving = true

    try {
      const formData = new FormData()
      formData.append("invite_uuid", this.inviteUuidValue)
      formData.append("book_id", this.bookIdValue.toString())
      formData.append("authenticity_token", this.getAuthenticityToken())

      this.selectedChoices.forEach((option, choiceId) => {
        formData.append(`responses[${choiceId}]`, option)
      })

      const response = await fetch("/participant/save_voting_response", {
        method: "POST",
        headers: {
          "X-CSRF-Token": this.getAuthenticityToken(),
          Accept: "application/json",
        },
        body: formData,
      })

      if (!response.ok) {
        throw new Error(`Failed to save responses: ${response.statusText}`)
      }

      const result = await response.json()
      if (!silent) {
        if (result.status === "ok") {
          this.showFlash(t("participant_voting_saved"), "success")
        } else {
          console.error("Error saving responses:", result.error)
          this.showFlash(t("participant_voting_error"), "danger")
        }
      }
    } catch (error) {
      console.error("Error saving responses:", error)
      if (!silent) {
        this.showFlash(t("participant_voting_error"), "danger")
      }
    } finally {
      this.isSaving = false
      if (this.saveQueued) {
        this.saveQueued = false
        const queuedSilent = this.queuedSilent
        this.queuedSilent = true
        this.saveAllChoices(queuedSilent)
      }
    }
  }

  private getAuthenticityToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (!token) {
      throw new Error("CSRF token not found")
    }
    return token
  }

  private showFlash(message: string, variant: "success" | "danger" = "success") {
    const main = document.querySelector("main#main")
    if (!main) return

    const existingAlerts = main.querySelectorAll(".alert[data-turbo-temporary]")
    existingAlerts.forEach((alert) => {
      alert.remove()
    })

    const alert = document.createElement("div")
    alert.className = `mb-org3 alert alert-${variant} alert-dismissible fade show zIndex1`
    alert.setAttribute("role", "alert")
    alert.setAttribute("data-turbo-temporary", "true")
    alert.innerHTML = `
      ${this.escapeHtml(message)}
      <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
    `

    main.insertBefore(alert, main.firstChild)

    setTimeout(() => {
      if (alert.parentNode) {
        alert.classList.remove("show")
        setTimeout(() => alert.remove(), 150)
      }
    }, 3000)
  }

  private escapeHtml(text: string): string {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
